"use client";

import * as React from "react";
import { AlertCircle, Clock } from "lucide-react";

import { cn } from "../../utils/cn";
import { usePickerLocale } from "../../utils/picker-locale";
import { Button } from "./button";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const HOURS_24 = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, "0"));
const HOURS_12 = Array.from({ length: 12 }, (_, hour) => String(hour + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, minute) => String(minute).padStart(2, "0"));
type Period = "AM" | "PM";

function to12HourTime(value: string): { hour: string; minute: string; period: Period } {
  const hour = Number(value.slice(0, 2));
  return {
    hour: String(hour % 12 || 12).padStart(2, "0"),
    minute: value.slice(3, 5),
    period: hour < 12 ? "AM" : "PM",
  };
}

function to24HourTime(hour: string, minute: string, period: Period): string {
  const convertedHour = (Number(hour) % 12) + (period === "PM" ? 12 : 0);
  return `${String(convertedHour).padStart(2, "0")}:${minute}`;
}

export interface TimePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  error?: string;
  placeholder?: string;
  hourLabel?: string;
  minuteLabel?: string;
  periodLabel?: string;
  amLabel?: string;
  pmLabel?: string;
  confirmLabel?: string;
  hourCycle?: 12 | 24;
  className?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
}

export function TimePicker({
  value,
  onChange,
  onBlur,
  label,
  error,
  placeholder: placeholderProp,
  hourLabel: hourLabelProp,
  minuteLabel: minuteLabelProp,
  periodLabel: periodLabelProp,
  amLabel: amLabelProp,
  pmLabel: pmLabelProp,
  confirmLabel: confirmLabelProp,
  hourCycle = 24,
  className,
  id,
  disabled,
  required,
}: TimePickerProps) {
  const { dir, strings } = usePickerLocale();
  const placeholder = placeholderProp ?? strings.selectTime;
  const hourLabel = hourLabelProp ?? strings.hour;
  const minuteLabel = minuteLabelProp ?? strings.minute;
  const periodLabel = periodLabelProp ?? strings.period;
  const amLabel = amLabelProp ?? strings.am;
  const pmLabel = pmLabelProp ?? strings.pm;
  const confirmLabel = confirmLabelProp ?? strings.setTime;
  const generatedId = React.useId();
  const triggerId = id ?? generatedId;
  const [open, setOpen] = React.useState(false);
  const [draftHour, setDraftHour] = React.useState<string | null>(null);
  const [draftMinute, setDraftMinute] = React.useState<string | null>(null);
  const [draftPeriod, setDraftPeriod] = React.useState<Period | null>(null);
  const selected = value && TIME_RE.test(value) ? value : undefined;
  const selected12 = selected ? to12HourTime(selected) : undefined;
  const hours = hourCycle === 12 ? HOURS_12 : HOURS_24;
  const selectedHourRef = React.useRef<HTMLButtonElement | null>(null);
  const selectedMinuteRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    selectedHourRef.current?.scrollIntoView({ block: "center" });
    selectedMinuteRef.current?.scrollIntoView({ block: "center" });
  }, [open, draftHour, draftMinute]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftHour(hourCycle === 12 ? (selected12?.hour ?? null) : (selected?.slice(0, 2) ?? null));
      setDraftMinute(selected12?.minute ?? null);
      setDraftPeriod(selected12?.period ?? null);
    } else {
      onBlur?.();
    }
    setOpen(nextOpen);
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label && (
        <Label htmlFor={triggerId} required={required}>
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            id={triggerId}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            data-empty={!selected}
            className={cn(
              "h-11 w-full justify-start gap-2 rounded-xl border-outline-variant/40 px-4 font-body font-normal shadow-none data-[empty=true]:text-surface-on-variant",
              "hover:bg-surface-container-high hover:text-surface-on",
              "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              error && "border-error focus-visible:ring-error",
              className,
            )}
          >
            <Clock className="size-4 shrink-0 text-primary" />
            {selected ? (
              // The digits stay LTR ("09:30") even in RTL; the AM/PM suffix sits after them in
              // reading order, so it follows the surrounding direction.
              <span className="flex items-center gap-1 tabular-nums text-surface-on">
                <span dir="ltr">
                  {hourCycle === 12 && selected12
                    ? `${selected12.hour}:${selected12.minute}`
                    : selected}
                </span>
                {hourCycle === 12 && selected12 && (
                  <span>{selected12.period === "AM" ? amLabel : pmLabel}</span>
                )}
              </span>
            ) : (
              <span className="truncate">{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "rounded-xl border-outline-variant/40 bg-surface-container-lowest p-3 shadow-ambient",
            hourCycle === 12 ? "w-72" : "w-56",
          )}
          align="start"
          sideOffset={6}
          dir={dir}
        >
          <div className={cn("grid gap-3", hourCycle === 12 ? "grid-cols-3" : "grid-cols-2")}>
            <div role="group" aria-label={hourLabel}>
              <p className="mb-2 text-center text-xs font-medium text-surface-on-variant">
                {hourLabel}
              </p>
              <div className="max-h-56 overflow-y-auto rounded-lg border border-outline-variant/40 p-1">
                {hours.map((hour) => (
                  <button
                    key={hour}
                    ref={draftHour === hour ? selectedHourRef : undefined}
                    type="button"
                    tabIndex={
                      draftHour === hour || (draftHour === null && hour === hours[0]) ? 0 : -1
                    }
                    aria-pressed={draftHour === hour}
                    onClick={() => setDraftHour(hour)}
                    onKeyDown={(event) => {
                      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
                      event.preventDefault();
                      const nextIndex = Math.max(
                        0,
                        Math.min(
                          hours.length - 1,
                          hours.indexOf(hour) + (event.key === "ArrowDown" ? 1 : -1),
                        ),
                      );
                      setDraftHour(hours[nextIndex]);
                      event.currentTarget.parentElement
                        ?.querySelectorAll<HTMLButtonElement>("button")
                        [nextIndex]?.focus();
                    }}
                    className={cn(
                      "flex h-9 w-full items-center justify-center rounded-md text-sm tabular-nums text-surface-on hover:bg-surface-container-high focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      draftHour === hour && "bg-primary text-primary-on hover:bg-primary",
                    )}
                  >
                    {hour}
                  </button>
                ))}
              </div>
            </div>
            <div role="group" aria-label={minuteLabel}>
              <p className="mb-2 text-center text-xs font-medium text-surface-on-variant">
                {minuteLabel}
              </p>
              <div className="max-h-56 overflow-y-auto rounded-lg border border-outline-variant/40 p-1">
                {MINUTES.map((minute) => (
                  <button
                    key={minute}
                    ref={draftMinute === minute ? selectedMinuteRef : undefined}
                    type="button"
                    disabled={hourCycle === 24 && draftHour === null}
                    tabIndex={
                      draftMinute === minute || (draftMinute === null && minute === "00") ? 0 : -1
                    }
                    aria-pressed={draftMinute === minute}
                    onKeyDown={(event) => {
                      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
                      event.preventDefault();
                      const nextIndex = Math.max(
                        0,
                        Math.min(59, Number(minute) + (event.key === "ArrowDown" ? 1 : -1)),
                      );
                      setDraftMinute(MINUTES[nextIndex]);
                      event.currentTarget.parentElement
                        ?.querySelectorAll<HTMLButtonElement>("button")
                        [nextIndex]?.focus();
                    }}
                    onClick={() => {
                      if (hourCycle === 12) {
                        setDraftMinute(minute);
                      } else if (draftHour !== null) {
                        onChange?.(`${draftHour}:${minute}`);
                        onBlur?.();
                        setOpen(false);
                      }
                    }}
                    className={cn(
                      "flex h-9 w-full items-center justify-center rounded-md text-sm tabular-nums text-surface-on hover:bg-surface-container-high focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40",
                      draftMinute === minute && "bg-primary text-primary-on hover:bg-primary",
                    )}
                  >
                    {minute}
                  </button>
                ))}
              </div>
            </div>
            {hourCycle === 12 && (
              <div role="group" aria-label={periodLabel}>
                <p className="mb-2 text-center text-xs font-medium text-surface-on-variant">
                  {periodLabel}
                </p>
                <div className="flex flex-col gap-1 rounded-lg border border-outline-variant/40 p-1">
                  {(["AM", "PM"] as const).map((period) => (
                    <button
                      key={period}
                      type="button"
                      aria-pressed={draftPeriod === period}
                      onClick={() => setDraftPeriod(period)}
                      className={cn(
                        "flex h-9 items-center justify-center rounded-md text-sm text-surface-on hover:bg-surface-container-high focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        draftPeriod === period && "bg-primary text-primary-on hover:bg-primary",
                      )}
                    >
                      {period === "AM" ? amLabel : pmLabel}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {hourCycle === 12 && (
            <Button
              type="button"
              className="mt-3 w-full"
              disabled={draftHour === null || draftMinute === null || draftPeriod === null}
              onClick={() => {
                if (draftHour === null || draftMinute === null || draftPeriod === null) return;
                onChange?.(to24HourTime(draftHour, draftMinute, draftPeriod));
                onBlur?.();
                setOpen(false);
              }}
            >
              {confirmLabel}
            </Button>
          )}
        </PopoverContent>
      </Popover>
      {error && (
        <p className="flex items-center gap-1.5 text-xs font-bold text-error" role="alert">
          <AlertCircle className="size-3.5 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}
